import { Component, OnDestroy, OnInit, ViewChild, inject, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { $state, restManager, util } from "app/ajs-upgraded-providers";
import { BarcodeManagerService, ConfigurationManagerService, EnvironmentInfoService, EntityManagerService, PaginatedResponse, ToolbarEventsService } from "src/app/core";
import { StockFe, StockModuleSections } from "./model/stock-model";
import { StockManagementTabsComponent } from "./stock-management-tabs/stock-management-tabs.component";
import { CommonModule } from "@angular/common";
import { AlertDialogService, OpenDialogsService, StockExportDialogService } from "src/app/dialogs";
import { BarcodeComponent, GridCellFormatterService, GridClickableTypeButtonComponent, GridFixedButtons, GridServerSideComponent, headersTranslate } from "src/app/shared/components";
import { QueryPaginationStock, TableData } from "src/app/shared/model/model";
import { IServerSideDatasource } from "ag-grid-community";
import { RawMaterials } from "tilby-models";
import { from } from "rxjs";
import { SharedModule } from "src/app/shared/shared.module";
import { subscribeInComponent } from "@tilby/tilby-ui-lib/utilities";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { MatChipListbox, MatChipsModule } from "@angular/material/chips";

@Component({
	standalone: true,
    selector: 'app-stock-management',
    templateUrl: './stock-management.component.html',
    styleUrls: ['./stock-management.component.scss'],
    imports: [ 
        CommonModule,
        MatChipsModule,
        SharedModule,
        StockManagementTabsComponent,
        BarcodeComponent
    ]
})
export class StockManagementComponent implements OnInit, OnDestroy {
    protected readonly util = inject(util);
    protected readonly state = inject($state);
    protected readonly restManager = inject(restManager);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    protected readonly translateService = inject(TranslateService);
    protected readonly entityManager = inject(EntityManagerService);
    private readonly openDialogsService = inject(OpenDialogsService);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly gridCellFormatterService = inject(GridCellFormatterService);
    private readonly stockExportDialogService = inject(StockExportDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly barcodeManagerService = inject(BarcodeManagerService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);

    stockLocations: any[] = [];
    @ViewChild('appGridComponent', {static: true}) gridRef!: GridServerSideComponent;
    @ViewChild('chipList') chipList?: MatChipListbox;

    private readonly tabs: StockModuleSections[] = ['items-simple', 'items-bom', 'combinations', 'raw-materials'];

    /** Columns that make no sense for a tab: BOM items have no own quantity, raw materials have no combination details */
    private readonly hiddenColumnsByTab: Partial<Record<StockModuleSections, (keyof StockFe)[]>> = {
        'items-bom': ['stock_quantity', 'value'],
        'raw-materials': ['details']
    };

    tabActive: StockModuleSections = 'items-simple';
    /** Server-side block size: the grid loads one block per scroll */
    readonly per_page = 50;
    currentStockLocationId = signal(1);
    
    datasource: IServerSideDatasource = this.buildDatasource();

    tableData: TableData[] = [
        {
            rowData$: null,
            dataType: new StockFe(),
            headersTranslate: headersTranslate.stock_showcase,
            columnsFormatter: this.gridCellFormatterService.stock,
            // value and details are computed client-side, so the server can't sort or filter on them
            sortingsMenuDisabled: new Map([['value', true], ['details', true]]),
            disabledInternalFilters: ['value', 'details']
        }
    ];

    fixedButtons: GridFixedButtons = {
        checkbox: {
            visible:false, 
            lockPosition: 'left'
        },
        customButton:{
            visible: false,
            cellRenderer: GridClickableTypeButtonComponent,
            lockPosition: 'left'
        }
    };

    searchText = "";

    /** Item (and combination) the last scanned barcode resolved to; replaces the raw barcode filter when set */
    private barcodeFilter?: { item_id: number, combination_id?: number };
    private lastBarcodeScan = 0;

    query!: QueryPaginationStock;

    constructor() {
        subscribeInComponent(this.toolbarEventsService.events, (event) => {
            switch(event.type) {
                case 'toolbar.clear_search_bar':
                    return this.resetSearchBar();
                case 'toolbar.search':
                    this.barcodeFilter = undefined;
                    this.searchText = event.search;
                    this.buildQuery();

                    // clearing the column filters already refetches
                    return event.search && this.clearColumnFilters() ? undefined : this.refresh();
                case 'grid.open_tool_panel':
                    return this.gridRef.openToolPanel(event.panel);

                case 'toolbar.search_barcode':
                    return this.searchByBarcode(event.barcode);
            }
        });
    }

    onBarcodeScanned(barcode: string) {
        if (document.querySelectorAll('.barcode-reader').length > 1) {
            return;
        }
        this.initBarcodeFilters(barcode);
    }

    initBarcodeFilters(barcode: string) {
        this.toolbarEventsService.events.next({ type: 'toolbar.search_barcode', barcode });
    }

    /** Shared by keyboard, camera and search bar scans */
    private async searchByBarcode(barcode: string) {
        this.toolbarEventsService.searchBarcode$.next(true);
        this.toolbarEventsService.searchBarValue$.next(barcode);
        this.toolbarEventsService.searchBarIcon$.next('');
        this.toolbarEventsService.searchBarIconFilled$.next('');
        this.clearExternalFilters();

        const scan = ++this.lastBarcodeScan;
        const barcodeFilter = await this.resolveBarcode(barcode);

        // a newer scan (or a reset) happened while resolving
        if (scan !== this.lastBarcodeScan) {
            return;
        }

        this.barcodeFilter = barcodeFilter;

        // Clearing the column filters already refetches. Otherwise server-side grids ignore the text here: this just triggers a refetch
        if (!this.clearColumnFilters()) {
            this.gridRef.onFilterTextBoxChanged<StockFe>(barcode, ['barcode']);
        }
    }

    /** Same resolution the old module used (scales, Tilby codes, local dictionary), with the raw barcode as a fallback */
    private async resolveBarcode(barcode: string) {
        if (this.tabActive === 'raw-materials') {
            return undefined;
        }

        try {
            const found = (await this.barcodeManagerService.searchBarcodeItem(barcode)).data[0];

            if (found?.item_id) {
                return { item_id: found.item_id, combination_id: found.combination_id };
            }

            if (this.barcodeManagerService.isTilbyItemBarcode(barcode)) {
                const item_id = parseInt(barcode.substring(4, 12)) || 0;

                return item_id ? { item_id } : undefined;
            }
        } catch(err) {
            console.error('Failed to resolve barcode', err);
        }

        return undefined;
    }

    private async scanWithCamera() {
        try {
            const barcode = await this.barcodeManagerService.openCameraBarcode();

            if (barcode) {
                this.initBarcodeFilters(barcode);
            }
        } catch(err) {
            // scan cancelled
        }
    }

    /** The grid ignores the search bar while column filters are active, so a search replaces them. Returns whether there was any */
    private clearColumnFilters() {
        const api = this.gridRef.gridApi;

        if (!api || !Object.keys(api.getFilterModel() ?? {}).length) {
            return false;
        }

        api.setFilterModel(null);

        return true;
    }

    clearExternalFilters() {
        this.toolbarEventsService.events.next({ type: 'toolbar.clear_external_filter' });
    }

    resetSearchBar() {
        this.lastBarcodeScan++;
        this.barcodeFilter = undefined;
        this.toolbarEventsService.searchBarcode$.next(false);
        this.toolbarEventsService.searchBarValue$.next('');
        this.toolbarEventsService.searchBarIcon$.next('tune');
        this.toolbarEventsService.searchBarIconFilled$.next('tune');
        this.toolbarEventsService.events.next({ type: 'toolbar.search', search: '' });
    }

    ngOnInit() {
        this.setModuleTitle();

        this.getStockLocations();

        this.getStockManagementPagedData();
        
        this.createToolbarButtons();

        this.toolbarEventsService.searchBarValue$.next('');
        this.toolbarEventsService.searchBarIcon$.next('tune');
        this.toolbarEventsService.searchBarIconFilled$.next('tune');
        this.toolbarEventsService.searchBarAction$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.toolbarEventsService.searchBarActionFilled$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.toolbarEventsService.searchBarcode$.next(false);
    }

    ngOnDestroy() {
        this.toolbarEventsService.searchBarcode$.next(false);
        this.toolbarEventsService.searchBarValue$.next('');
        this.toolbarEventsService.searchBarIcon$.next('tune');
        this.toolbarEventsService.searchBarIconFilled$.next('tune');
        this.toolbarEventsService.buttons$.next({ barButtons: [], panelButtons: [] });
    }

    async getStockLocations() {
        try {
            const stockLocations = await this.restManager.getList('stock_locations');

            for(const location of stockLocations) {
                if(location.id <= 100) {
                    location.name = this.translateService.instant(`STOCK.LOCATIONS.${location.name}`);
                }
            }

            this.stockLocations = stockLocations;
        } catch(err) {
            console.error('Failed to fetch stock locations', err);
        }
    }

    setModuleTitle() {
        this.toolbarEventsService.moduleTitle.next("STOCK");
    }

    changeTab(tabIndex: number) {
        this.tabActive = this.tabs[tabIndex] ?? 'items-simple';

        this.updateTabColumns();
        this.getStockManagementPagedData();
    }

    private updateTabColumns() {
        const hidden = this.hiddenColumnsByTab[this.tabActive] ?? [];
        const managed = new Set(Object.values(this.hiddenColumnsByTab).flat());

        this.gridRef.gridApi?.setColumnsVisible([...managed].filter(col => !hidden.includes(col)), true);
        this.gridRef.gridApi?.setColumnsVisible(hidden, false);
    }

    createToolbarButtons() {
        this.toolbarEventsService.buttons$.next({
            barButtons: [
                {
                    isIt: () => !!this.environmentInfoService.isCameraBarcodeSupported(),
                    name: 'camera',
                    icon: () => 'photo_camera',
                    click: () => this.scanWithCamera()
                },
                {
                    isIt: () => true,
                    name: 'email',
                    icon: () => 'email',
                    click: () => this.exportStock()
                },
                {
                    isIt: () => true,
                    name: 'show_summary',
                    icon: () => 'poll',
                    click: () => this.showSummary()
                },
                {
                    isIt: () => true,
                    name: 'import_export',
                    icon: () => 'import_export',
                    click: () => this.goToMovements()
                }
            ],
            panelButtons: []
        });
    }

    async exportStock() {
        const result = await this.stockExportDialogService.openDialog({
            email: this.configurationManagerService.getPreference('stock.export.default_email') || '',
            allowDate: this.configurationManagerService.isFunctionEnabledOptin('stock.show_export_at_date')
        });

        if(!result) {
            return;
        }

        const answer = {
            ...result,
            stock_location_id: this.currentStockLocationId()
        };

        try {
            await this.restManager.getOne("stock", "export", answer);

            this.openDialogsService.openSnackBarTilby('STOCK.MANAGEMENT.EMAIL_SENT', 'TOAST.SAVED.ACTION', { duration: 3000 });
        } catch(error: any) {
            const errMsg = error?.data?.error?.message === 'email param not found'
                ? this.translateService.instant('STOCK.MANAGEMENT.NOT_VALID_EMAIL')
                : this.translateService.instant('STOCK.MANAGEMENT.ERROR_WHILE_SENDING');

            this.openDialogsService.openSnackBarTilby(errMsg, 'TOAST.SAVED.ACTION', { duration: 5000 });
        }
    }

    async showSummary() {
        let summary: any;

        try {
            summary = (await this.restManager.getList('stock_summary'))?.[0];
        } catch(err) {
            console.error('Failed to fetch stock summary', err);
        }

        if (!summary) {
            return;
        }

        const t = (key: string) => this.translateService.instant(key);
        const stockValue = this.tilbyCurrencyPipe.transform(summary.stock_value);
        const stockAmount = Number.parseInt(summary.stock_amount, 10);

        this.alertDialogService.openDialog({
            data: {
                titleLabel: 'STOCK.SUMMARY.TITLE',
                messageLabel: `${t('STOCK.SUMMARY.VALUE')} ${stockValue}\n${t('STOCK.SUMMARY.TOTAL_STOCKS')} ${stockAmount}`
            }
        });
    }

    goToMovements() {
        this.state.go(this.configurationManagerService.isModuleAngular('stock.movements')
            ? 'app.new.stock.movements'
            : 'app.stock.movements');
    }

    getStockManagementPagedData() {
        this.buildQuery();
        this.refresh();
    }

    private buildQuery() {
        this.query = {
            pagination: true,
            per_page: this.per_page,
            page: 0,
            barcode: null,
            stock_location_id: this.currentStockLocationId(),
        };

        let xQuery: string[] = [];

        if (this.searchText) {
            let encodedSearchText = this.util.fixedEncodeURIComponent(this.searchText);

            xQuery.push(`OR(name_like=${encodedSearchText},item_id=${encodedSearchText},item.sku_like=${encodedSearchText},item.department.name_like=${encodedSearchText},item.category.name_like=${encodedSearchText},code=${encodedSearchText})`);
        }

        if (xQuery.length) {
            this.query.x_query = xQuery;
        }

        switch (this.tabActive) {
        case 'items-simple':
                Object.assign(this.query, {
                    item_id: 'notnull',
                    combination_id: 'null',
                    raw_material_id: 'null',
                    stock_quantity: 'notnull'
                });
                break;
            case 'items-bom':
                Object.assign(this.query, {
                    item_id: 'notnull',
                    combination_id: 'null',
                    raw_material_id: 'null',
                    stock_quantity: 'null'
                });
                break;
            case 'combinations':
                Object.assign(this.query, {
                    item_id: 'notnull',
                    combination_id: 'notnull',
                    raw_material_id: 'null',
                });
                break;
            case 'raw-materials':
                Object.assign(this.query, {
                    item_id: 'null',
                    combination_id: 'null',
                    raw_material_id: 'notnull',
                });
                break;
            default:
                break;
        }
    }

    refresh() {
        // the datasource reads this.query lazily, so a purge is enough to refetch with the new query
        this.gridRef.refreshGrid();
    }

    private buildDatasource(): IServerSideDatasource {
        return {
            getRows: (params) => {
                // the grid clears the search bar when column filters are active: drop our copy too, or it would keep filtering unseen
                if (Object.keys(params.request.filterModel ?? {}).length) {
                    this.clearSearchState();
                }

                this.gridRef.getRowsForServersideDatasource(
                    params,
                    ["stock", this.query],
                    (a, b?) => from(this.fetchStock(a, b)),
                    {
                        sort_type: "asc",
                        sort_col: 'name'
                    }
                );
            }
        };
    }

    private clearSearchState() {
        this.searchText = '';
        this.barcodeFilter = undefined;
        delete this.query.x_query;
        this.toolbarEventsService.searchBarcode$.next(false);
    }

    private async fetchStock(a: string, b?: QueryPaginationStock): Promise<PaginatedResponse<StockFe>> {
        const response: PaginatedResponse<StockFe> | StockFe[] = await this.restManager.getList(a, this.applyBarcodeFilter(b));
        const paginated = Array.isArray(response) ? null : response;
        const results = paginated?.results ?? (Array.isArray(response) ? response : []);

        const costs = await this.getCosts(results);

        const rows: StockFe[] = results.map(row => {
            const cost = costs.get(row.id);
            // BOM items have no own quantity
            const hasQuantity = row.stock_quantity != null && row.stock_quantity !== '';

            return {
                ...row,
                value: cost && hasQuantity ? this.tilbyCurrencyPipe.transform(+row.stock_quantity * cost) ?? '' : '',
                stock_quantity: hasQuantity && row.unit ? `${row.stock_quantity} ${row.unit}` : row.stock_quantity ?? '',
                details: this.getDetails(row)
            };
        });

        return { ...paginated, results: rows, total: paginated?.total ?? rows.length } as PaginatedResponse<StockFe>;
    }

    /** Swaps the raw barcode the grid adds for the item the barcode resolved to */
    private applyBarcodeFilter(query?: QueryPaginationStock) {
        if (!query || !this.barcodeFilter || this.tabActive === 'raw-materials') {
            return query;
        }

        const { barcode, ...rest } = query;
        const { item_id, combination_id } = this.barcodeFilter;

        return {
            ...rest,
            item_id,
            ...(this.tabActive === 'combinations' && combination_id && { combination_id })
        } as unknown as QueryPaginationStock;
    }

    getDetails(row: StockFe) {
        return [row.options_values, row.combination].filter(Boolean).join(" ");
    }

    /** Loads the costs once per page and returns them keyed by stock row id */
    private async getCosts(rows: StockFe[]): Promise<Map<number, number>> {
        const costs = new Map<number, number>();

        try {
            if (this.tabActive === 'raw-materials') {
                const rawMaterials = await this.fetchRawMaterials(rows);

                for (const row of rows) {
                    const cost = rawMaterials.get(row.raw_material_id)?.cost;

                    if (cost) {
                        costs.set(row.id, cost);
                    }
                }
            } else {
                const items = await this.entityManager.items.fetchMapFromIdsOffline(rows.map(row => row.item_id));

                for (const row of rows) {
                    const cost = items[row.item_id]?.cost;

                    if (cost) {
                        costs.set(row.id, cost);
                    }
                }
            }
        } catch(err) {
            console.error('Failed to fetch stock costs', err);
        }

        return costs;
    }

    private async fetchRawMaterials(rows: StockFe[]): Promise<Map<number, RawMaterials>> {
        const ids = new Set(rows.map(row => row.raw_material_id).filter(id => id != null));
        const rawMaterials = new Map<number, RawMaterials>();

        await Promise.all([...ids].map(async id => {
            try {
                const rawMaterial = await this.entityManager.rawMaterials.fetchOneOfflineFirst(id);

                if (rawMaterial) {
                    rawMaterials.set(id, rawMaterial);
                }
            } catch(err) {}
        }));

        return rawMaterials;
    }

    onFilterStatusChange(newValue: string) {
        if(!newValue) {
            // clicking the selected chip deselects it: a location must always be selected
            if(this.chipList) {
                this.chipList.value = this.currentStockLocationId();
            }

            return;
        }

        this.currentStockLocationId.set(+newValue);

        this.getStockManagementPagedData();
    }
}
